import axios from 'axios';
import { isTokenExpired } from '../utils/jwtHelper';
import { useUserStore } from '@/stores/user';

/**
 * 认证服务
 * 处理所有与用户认证相关的API请求
 */
class AuthService {
  constructor() {
    // 添加缓存相关属性
    this.lastVerifyTime = 0;
    this.lastVerifyResult = false;
    this.verifyTimeoutMs = 10000; // 10秒内不重复验证
    this.verifyPromise = null;
  }

  /**
   * 获取真实客户端IP
   * @param {Object} headers 响应头
   * @returns {string|null} 客户端IP
   */
  getClientIp(headers) {
    // 按优先级尝试不同的header
    const ipHeaders = [
      'x-original-forwarded-for', // 原始转发IP
      'x-forwarded-for',         // 代理转发IP链
      'x-real-ip',              // Nginx配置的真实IP
      'x-client-ip',            // 客户端IP
      'cf-connecting-ip',       // Cloudflare IP
      'true-client-ip',         // 真实客户端IP
      'remote-addr'             // 远程地址
    ];

    for (const header of ipHeaders) {
      const value = headers[header];
      if (value) {
        // 如果是IP链（如x-forwarded-for可能包含多个IP），取第一个
        const ips = value.split(',').map(ip => ip.trim());
        // 返回第一个非内网IP
        const realIp = ips.find(ip => !this.isInternalIp(ip));
        if (realIp) {
          return realIp;
        }
      }
    }

    return null;
  }

  /**
   * 判断是否为内网IP
   * @param {string} ip IP地址
   * @returns {boolean} 是否为内网IP
   */
  isInternalIp(ip) {
    if (!ip) return false;
    
    // 检查是否为内网IP段
    return /^(127\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.|192\.168\.)/.test(ip);
  }

  /**
   * 验证令牌有效性
   * @param {string} token JWT令牌
   * @returns {Promise<boolean>} 令牌是否有效
   */
  async verifyToken(token) {
    if (!token) return false;
    
    // 先检查token是否过期
    if (isTokenExpired(token)) {
      console.log('令牌已过期');
      return false;
    }
    
    const now = Date.now();
    
    // 使用缓存结果，如果上次验证是在短时间内且结果为有效
    if (now - this.lastVerifyTime < this.verifyTimeoutMs && this.lastVerifyResult) {
      // console.log('使用缓存的验证结果，避免重复请求');
      return this.lastVerifyResult;
    }
    
    // 如果已经有一个验证请求在进行中，返回该请求的Promise
    if (this.verifyPromise) {
      // console.log('验证请求正在进行中，复用当前请求');
      return this.verifyPromise;
    }
    
    // 创建新的验证请求
    this.verifyPromise = (async () => {
      try {
        const response = await axios.get('/newapi/user/verify-token', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const isValid = response.data.code === 200;
        
        // 如果验证成功，保存用户ID和IP
        if (isValid) {
          const userStore = useUserStore();
          
          // 保存用户ID
          if (response.data.userId) {
            userStore.setUserId(response.data.userId);
          }
          
          // 获取并保存用户IP
          const userIp = this.getClientIp(response.headers);
          if (userIp) {
            console.log('获取到用户真实IP:', userIp);
            userStore.setUserIp(userIp);
            
            // 立即将IP保存到localStorage
            localStorage.setItem('userIp', userIp);
            
            // 打印存储状态
            console.log('IP存储状态：', {
              storeIp: userStore.userIp,
              localStorageIp: localStorage.getItem('userIp')
            });
          } else {
            console.warn('未能获取到用户IP');
          }
        }
        
        // 更新缓存状态
        this.lastVerifyTime = Date.now();
        this.lastVerifyResult = isValid;
        
        return isValid;
      } catch (error) {
        console.error('验证令牌失败:', error);
        return false;
      } finally {
        // 验证完成后清除Promise引用
        setTimeout(() => {
          this.verifyPromise = null;
        }, 100);
      }
    })();
    
    return this.verifyPromise;
  }
  
  /**
   * 获取微信登录URL
   * @param {string} returnUrl 登录成功后的回调URL
   * @returns {string} 微信登录URL
   */
  getWxLoginUrl(returnUrl) {
    const currentUrl = returnUrl || window.location.origin + window.location.pathname;
    const encodedReturnUrl = encodeURIComponent(currentUrl);
    return `https://identity.gdtvdv.com/wxlogin.html?return_url=${encodedReturnUrl}`;
  }
  
  /**
   * 从URL获取token
   * @returns {string|null} URL中的token参数值
   */
  getTokenFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }
  
  /**
   * 清除URL中的token参数
   */
  clearTokenFromUrl() {
    if (window.history.replaceState) {
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }
  
  /**
   * 退出登录
   * @returns {Promise<void>}
   */
  async logout(token) {
    try {
      // 可选: 调用后端登出API
      /*
      if (token) {
        await axios.post('/newapi/user/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      */
      
      // 清除本地存储
      localStorage.removeItem('userToken');
    } catch (error) {
      console.error('退出登录失败:', error);
    }
  }

  /**
   * 获取签名
   * @param {string} token JWT令牌
   * @param {string} url 当前页面URL
   * @returns {Promise<Object>} 签名信息
   */
  async getSignature(token, url) {
    if (!token) return null;
    
    try {
      const response = await axios.get('/newapi/user/get-signuature', {
        params: {
          url: encodeURIComponent(url)
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.data.code === 200 ? response.data.data : null;
    } catch (error) {
      console.error('获取签名失败:', error);
      return null;
    }
  }
}

export const authService = new AuthService(); 